import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Wrench, FileText, Eye, CheckCircle2, CheckCircle, ArrowLeft, Download, MapPin,
  AlertCircle, Loader2, Lock, ListChecks,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useLanguage } from "@/context/LanguageContext";
import { apiClient, backendUrl } from "@/lib/api";
import { getBlockNumber } from "@/lib/blockMapping";
import { HOME_TYPES } from "@/lib/formConfigs";
import { fmtMoney, cleanNum } from "@/lib/money";

const ACCENT = "#059669";
const inputCls =
  "h-10 w-full rounded-lg border border-slate-200 bg-white px-3 text-sm text-slate-900 outline-none focus:border-emerald-500 disabled:bg-slate-50 disabled:text-slate-500";
const Label = ({ children }) => (
  <label className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-slate-500">{children}</label>
);

export default function MechanicProgressForm({ unitNumber }) {
  const { user } = useAuth();
  const { t, language } = useLanguage();
  const navigate = useNavigate();

  const [estNum, setEstNum] = useState("");
  const [estNumError, setEstNumError] = useState("");
  const [date, setDate] = useState(new Date().toISOString().split("T")[0]);
  const [subject, setSubject] = useState("");
  const [contractorName, setContractorName] = useState("");
  const [contractorPhone, setContractorPhone] = useState("");

  const [houseType, setHouseType] = useState("");
  const [houseNumber, setHouseNumber] = useState("");

  const [progress, setProgress] = useState(null);
  const [progressLoading, setProgressLoading] = useState(false);
  const [progressError, setProgressError] = useState("");
  const [selected, setSelected] = useState([]);

  const [step, setStep] = useState("form");
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [excelUrl, setExcelUrl] = useState("");
  const [done, setDone] = useState(false);

  const disabled = step !== "form";

  const blockNumber = useMemo(
    () => (houseType && houseNumber ? getBlockNumber(houseType, houseNumber) : ""),
    [houseType, houseNumber]
  );
  const resolvedUnit = houseType && houseNumber ? `${houseType}-${houseNumber}` : unitNumber || "";

  const loadProgress = useCallback(async () => {
    if (!houseType || !houseNumber) {
      setProgress(null);
      return;
    }
    setProgressLoading(true);
    setProgressError("");
    try {
      const data = await apiClient.get(
        `/mechanic/house-progress?house_type=${houseType}&house_number=${encodeURIComponent(houseNumber)}&unit_number=${encodeURIComponent(resolvedUnit)}`
      );
      setProgress(data);
      setSelected([]);
    } catch (e) {
      setProgress(null);
      setProgressError(e.message || "Could not load house progress");
    } finally {
      setProgressLoading(false);
    }
  }, [houseType, houseNumber, resolvedUnit]);

  useEffect(() => {
    const id = setTimeout(loadProgress, 350);
    return () => clearTimeout(id);
  }, [loadProgress]);

  const items = progress?.items || [];
  const toggle = (code) => {
    if (disabled) return;
    setSelected((prev) => (prev.includes(code) ? prev.filter((c) => c !== code) : [...prev, code]));
  };

  const selectedItems = items.filter((it) => selected.includes(it.item_code) && it.status === "available");
  const selPct = cleanNum(selectedItems.reduce((s, it) => s + Number(it.percentage || 0), 0));
  const selTotal = cleanNum(selectedItems.reduce((s, it) => s + Number(it.price || 0), 0));

  const validate = async () => {
    setError("");
    setEstNumError("");
    const numVal = parseInt(estNum, 10);
    if (!estNum || isNaN(numVal) || numVal <= 0) {
      setEstNumError(t("estimation_number_required"));
      return false;
    }
    if (!subject.trim()) {
      setError(`${t("subject")} ${t("is_required")}`);
      return false;
    }
    if (!houseType || !houseNumber) {
      setError(t("invalid_house_number"));
      return false;
    }
    if (selectedItems.length === 0) {
      setError(t("select_work_items"));
      return false;
    }
    try {
      const check = await apiClient.get(
        `/estimations/count?unit_number=${resolvedUnit}&category=mechanic&estimation_number=${numVal}`
      );
      if (check.is_valid === false) {
        setEstNumError(check.error || t("estimation_number_duplicate"));
        return false;
      }
    } catch {
      /* proceed */
    }
    return true;
  };

  const basePayload = () => ({
    category: "mechanic",
    is_mechanic_progress: true,
    mechanic_house_type: houseType,
    house_number: houseNumber,
    unit_number: resolvedUnit,
    item_codes: selectedItems.map((it) => it.item_code),
    estimation_number: parseInt(estNum, 10),
    date,
    subject,
    engineer_name: user?.name || "",
    contractor_name: contractorName,
    contractor_phone: contractorPhone,
  });

  const handlePreview = async () => {
    if (!(await validate())) return;
    setPreviewLoading(true);
    setError("");
    try {
      const res = await apiClient.post("/estimations/preview-excel", basePayload());
      if (res.preview_url) {
        setPreviewUrl(`${backendUrl()}${res.preview_url}`);
        setStep("previewing");
      }
    } catch (e) {
      setError(e.message || "Preview failed");
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleConfirm = async () => {
    setSubmitting(true);
    setError("");
    try {
      const created = await apiClient.post("/estimations", {
        unit_number: resolvedUnit,
        data: basePayload(),
      });
      try {
        const docRes = await apiClient.post(`/estimations/${created.id}/generate-documents`, {});
        if (docRes.excel_url) setExcelUrl(docRes.excel_url);
      } catch {
        /* doc gen best-effort */
      }
      setStep("confirmed");
      setDone(true);
    } catch (e) {
      setError(e.message || t("failed_submit"));
      setStep("form");
    } finally {
      setSubmitting(false);
    }
  };

  const nameOf = (it) => it.name_ku;

  return (
    <div className="mx-auto max-w-4xl pb-10">
      <div className="mb-5 flex items-center gap-3 rounded-2xl border p-5" style={{ backgroundColor: `${ACCENT}0d`, borderColor: `${ACCENT}33` }}>
        <div className="flex h-12 w-12 items-center justify-center rounded-xl" style={{ backgroundColor: `${ACCENT}20` }}>
          <Wrench className="h-6 w-6" style={{ color: ACCENT }} />
        </div>
        <div>
          <h1 className="text-xl font-bold text-slate-900">{t("mechanic_progress_form")}</h1>
          <p className="text-sm text-slate-500">{t("mechanic_work")}</p>
        </div>
      </div>

      {step === "previewing" ? (
        <div className="mb-5 rounded-2xl border-2 border-emerald-500 bg-emerald-50 p-5">
          <div className="mb-3 flex items-center gap-3">
            <FileText className="h-6 w-6 text-emerald-600" />
            <div>
              <div className="text-base font-bold text-emerald-700">{t("preview_ready")}</div>
              <div className="text-xs text-emerald-600">{t("preview_note")}</div>
            </div>
          </div>
          <a href={previewUrl} target="_blank" rel="noreferrer" data-testid="download-preview-btn" className="mb-3 flex items-center justify-center gap-2 rounded-xl bg-emerald-600 py-3 text-sm font-semibold text-white">
            <Download className="h-4 w-4" /> {t("download_excel")}
          </a>
          <div className="flex gap-3">
            <button onClick={() => { setStep("form"); setPreviewUrl(""); }} className="flex flex-1 items-center justify-center gap-1.5 rounded-xl border-2 py-3 text-sm font-semibold" style={{ borderColor: ACCENT, color: ACCENT }}>
              <ArrowLeft className="h-4 w-4" /> {t("back_to_edit")}
            </button>
            <button data-testid="mech-confirm-btn" onClick={handleConfirm} disabled={submitting} className="flex flex-[2] items-center justify-center gap-1.5 rounded-xl py-3 text-sm font-semibold text-white disabled:opacity-70" style={{ backgroundColor: ACCENT }}>
              {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />} {t("confirm_and_submit")}
            </button>
          </div>
        </div>
      ) : null}

      {done ? (
        <div className="mb-5 flex flex-col items-center rounded-2xl border-2 border-emerald-500 bg-emerald-50 p-8 text-center">
          <CheckCircle className="mb-3 h-14 w-14 text-emerald-500" />
          <div className="text-lg font-bold text-slate-900">{t("success")}</div>
          <div className="mt-1 text-sm text-slate-600">{t("estimation_saved")}</div>
          <div className="mt-5 flex gap-3">
            {excelUrl ? (
              <a href={excelUrl} target="_blank" rel="noreferrer" className="flex items-center gap-2 rounded-xl px-5 py-2.5 text-sm font-semibold text-white" style={{ backgroundColor: ACCENT }}>
                <Download className="h-4 w-4" /> {t("download_excel")}
              </a>
            ) : null}
            <button onClick={() => navigate(-1)} className="rounded-xl border border-slate-200 bg-white px-5 py-2.5 text-sm font-semibold text-slate-600">{t("ok")}</button>
          </div>
        </div>
      ) : null}

      {!done ? (
        <>
          {/* Top info */}
          <div className={`mb-5 rounded-2xl border border-slate-200 bg-white p-6 ${disabled ? "opacity-70" : ""}`}>
            <div className="mb-4 flex items-center gap-2">
              <FileText className="h-5 w-5" style={{ color: ACCENT }} />
              <h3 className="text-base font-semibold text-slate-900">{t("top_info")}</h3>
            </div>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <Label>{t("estimation_number")}</Label>
                <input data-testid="estimation-number-input" className={inputCls} style={estNumError ? { borderColor: "#DC2626" } : {}} value={estNum} onChange={(e) => { setEstNum(e.target.value.replace(/[^0-9]/g, "")); setEstNumError(""); }} placeholder={t("enter_estimation_number")} inputMode="numeric" disabled={disabled} />
                {estNumError ? <p className="mt-1 text-xs text-red-600">{estNumError}</p> : null}
              </div>
              <div>
                <Label>{t("subject")}</Label>
                <input data-testid="subject-input" className={inputCls} value={subject} onChange={(e) => setSubject(e.target.value)} placeholder={t("enter_subject")} disabled={disabled} />
              </div>
              <div>
                <Label>{t("date_label")}</Label>
                <input type="date" className={inputCls} value={date} onChange={(e) => setDate(e.target.value)} disabled={disabled} />
              </div>
              <div>
                <Label>{t("contractor_name")}</Label>
                <input className={inputCls} value={contractorName} onChange={(e) => setContractorName(e.target.value)} placeholder={t("enter_contractor_name")} disabled={disabled} />
              </div>
              <div>
                <Label>{t("contractor_phone")}</Label>
                <input className={inputCls} value={contractorPhone} onChange={(e) => setContractorPhone(e.target.value)} placeholder={t("enter_phone")} disabled={disabled} />
              </div>
              <div>
                <Label>{t("engineer_name_label")}</Label>
                <div className="flex h-10 items-center rounded-lg border border-slate-200 bg-slate-100 px-3 text-sm text-slate-700">{user?.name || "-"}</div>
              </div>
            </div>
          </div>

          {/* House selector */}
          <div className={`mb-5 rounded-2xl border border-slate-200 bg-white p-6 ${disabled ? "opacity-70" : ""}`}>
            <div className="mb-4 flex items-center gap-2">
              <MapPin className="h-5 w-5" style={{ color: ACCENT }} />
              <h3 className="text-base font-semibold text-slate-900">{t("house_type")} / {t("house_number")}</h3>
            </div>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
              <div>
                <Label>{t("house_type")}</Label>
                <div className="flex gap-2">
                  {HOME_TYPES.map((ht) => (
                    <button key={ht} type="button" data-testid={`mech-house-type-${ht}`} disabled={disabled} onClick={() => setHouseType(ht)} className={`flex-1 rounded-lg border py-2 text-sm font-semibold ${houseType === ht ? "border-transparent text-white" : "border-slate-200 bg-white text-slate-500"}`} style={houseType === ht ? { backgroundColor: ACCENT } : {}}>
                      {ht}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <Label>{t("house_number")}</Label>
                <input data-testid="mech-house-number-input" className={inputCls} value={houseNumber} onChange={(e) => setHouseNumber(e.target.value.replace(/[^0-9]/g, ""))} placeholder={t("enter_house_number")} inputMode="numeric" disabled={disabled} />
              </div>
              <div>
                <Label>{t("block_number")}</Label>
                <div className="flex h-10 items-center gap-2 rounded-lg border bg-slate-100 px-3 text-sm" style={{ borderColor: blockNumber ? ACCENT : "#E2E8F0" }}>
                  {blockNumber ? (
                    <><MapPin className="h-4 w-4 text-emerald-600" /><span className="font-semibold text-emerald-600">{blockNumber}</span></>
                  ) : (
                    <span className="text-slate-400">{t("auto_calculated")}</span>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* House progress + checklist */}
          <div className={`mb-5 rounded-2xl border border-slate-200 bg-white p-6 ${disabled ? "opacity-70" : ""}`}>
            <div className="mb-4 flex items-center gap-2">
              <ListChecks className="h-5 w-5" style={{ color: ACCENT }} />
              <h3 className="flex-1 text-base font-semibold text-slate-900">{t("work_items_label")}</h3>
            </div>

            {!progress && !progressLoading ? (
              <p className="py-6 text-center text-sm text-slate-400">{progressError || t("enter_house_first")}</p>
            ) : null}
            {progressLoading ? (
              <p className="py-6 text-center text-sm text-slate-400"><Loader2 className="mx-auto h-5 w-5 animate-spin" /> {t("loading_house_progress")}</p>
            ) : null}

            {progress ? (
              <>
                {/* Progress bar */}
                <div className="mb-5 rounded-xl bg-slate-50 p-4" data-testid="house-progress">
                  <div className="mb-2 flex items-center justify-between text-sm">
                    <span className="font-semibold text-slate-700">{t("house_progress")}</span>
                    <span className="font-bold" style={{ color: ACCENT }} data-testid="completed-pct">{progress.completed_pct}%</span>
                  </div>
                  <div className="h-3 w-full overflow-hidden rounded-full bg-slate-200">
                    <div className="h-full rounded-full transition-all" style={{ width: `${Math.min(100, progress.completed_pct)}%`, backgroundColor: ACCENT }} />
                  </div>
                  <div className="mt-3 grid grid-cols-2 gap-3 text-xs sm:grid-cols-4">
                    <Stat label={t("base_price_label")} value={fmtMoney(progress.base_price, "USD")} />
                    <Stat label={t("completed_label")} value={fmtMoney(progress.completed_value, "USD")} />
                    <Stat label={t("remaining_label")} value={`${progress.remaining_pct}%`} />
                    <Stat label={t("remaining_label")} value={fmtMoney(progress.remaining_value, "USD")} />
                  </div>
                  {progress.base_price_changed ? (
                    <p className="mt-2 text-xs text-amber-600">{t("price_changed_note")}</p>
                  ) : null}
                </div>

                {progress.is_complete ? (
                  <p className="rounded-xl bg-emerald-50 px-4 py-3 text-center text-sm font-semibold text-emerald-700">{t("house_complete")}</p>
                ) : (
                  <div className="space-y-2">
                    {items.map((it) => {
                      const isCompleted = it.status === "completed";
                      const isSel = selected.includes(it.item_code);
                      return (
                        <button
                          key={it.item_code}
                          type="button"
                          data-testid={`mech-item-${it.item_code}`}
                          disabled={isCompleted || disabled}
                          onClick={() => toggle(it.item_code)}
                          className={`flex w-full items-center gap-3 rounded-xl border p-3 text-start transition-colors ${
                            isCompleted
                              ? "border-slate-200 bg-slate-50 opacity-70"
                              : isSel
                              ? "border-emerald-500 bg-emerald-50"
                              : "border-slate-200 bg-white hover:border-emerald-300"
                          }`}
                        >
                          <span className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-md border ${isSel && !isCompleted ? "border-transparent bg-emerald-500 text-white" : "border-slate-300 bg-white text-transparent"}`}>
                            {isCompleted ? <Lock className="h-3.5 w-3.5 text-slate-400" /> : <CheckCircle2 className="h-4 w-4" />}
                          </span>
                          <span className="flex-1 text-sm font-medium text-slate-800">{nameOf(it)}</span>
                          <span className="rounded-md bg-slate-100 px-2 py-0.5 text-xs font-semibold text-slate-500">{it.percentage}%</span>
                          <span className="w-24 text-end text-sm font-semibold" style={{ color: isCompleted ? "#94A3B8" : ACCENT }}>{fmtMoney(it.price, "USD")}</span>
                          {isCompleted ? <span className="text-[10px] font-semibold uppercase text-slate-400">{t("locked_completed")}</span> : null}
                        </button>
                      );
                    })}
                  </div>
                )}

                {/* Selected summary */}
                {!progress.is_complete ? (
                  <div className="mt-4 flex items-center justify-between rounded-xl bg-emerald-50 px-4 py-3">
                    <span className="text-sm font-semibold text-emerald-700">{t("selected_total")} ({selPct}%)</span>
                    <span className="text-lg font-bold text-emerald-700" data-testid="selected-total">{fmtMoney(selTotal, "USD")}</span>
                  </div>
                ) : null}
              </>
            ) : null}
          </div>

          {error ? (
            <div className="mb-4 flex items-center gap-2 rounded-xl bg-red-50 px-4 py-3 text-sm text-red-600">
              <AlertCircle className="h-4 w-4" /> {error}
            </div>
          ) : null}

          {step === "form" ? (
            <button data-testid="mech-preview-btn" onClick={handlePreview} disabled={previewLoading} className="flex w-full items-center justify-center gap-2 rounded-xl border-2 py-4 text-base font-semibold disabled:opacity-70" style={{ borderColor: ACCENT, color: ACCENT, backgroundColor: `${ACCENT}0a` }}>
              {previewLoading ? <Loader2 className="h-5 w-5 animate-spin" /> : <Eye className="h-5 w-5" />} {t("preview_estimation")}
            </button>
          ) : null}
        </>
      ) : null}
    </div>
  );
}

const Stat = ({ label, value }) => (
  <div>
    <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">{label}</div>
    <div className="text-sm font-semibold text-slate-800">{value}</div>
  </div>
);
